/**
 * @file リーダーのヘッダーに収まらない操作を畳む、⋯のメニューである。
 *
 * @what: ⋯だけのボタンを押すと、狭い画面でヘッダーから外した操作と表示の切り替えが下に開く。
 *   開くと最初の項目へ焦点が移り、↑↓ で移動、Esc と外側のクリックで閉じる (Esc はボタンへ
 *   焦点を戻す)。項目を選ぶと閉じる。ページ番号の入力だけは項目でなく、一覧の下に置く。
 * @why: スマホの縦画面ではヘッダーが5行を超え、紙面が半分近くまで縮んでいた (代表の指摘、
 *   2026-09-11)。表に残すのは教材から選ぶ操作1つ (ページの面では「範囲を囲む」) と「メモ」だけに
 *   し、残りをここへ入れる。
 *   見た目と操作は本だなカードのメニュー (ShelfCardMenu) に揃える。
 * @failure: 描画のみ。入れるものが1つも無いときは、呼び出し側がこの部品を出さない。
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ViewerControl, ViewerPanel } from "./FullscreenViewer";
import "./study.css";

export function ViewerMenu({
  controls,
  panels,
  openPanelId,
  onSelectPanel,
  extra,
}: {
  controls: readonly ViewerControl[];
  panels: readonly ViewerPanel[];
  openPanelId: string | null;
  onSelectPanel: (id: string) => void;
  /** 一覧の下に置くもの (ページ番号の入力)。 */
  extra?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const menuItems = () =>
    Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"], [role="menuitemcheckbox"]',
      ) ?? [],
    );

  useEffect(() => {
    if (!open) return;
    menuItems()[0]?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // リーダーの Esc (紙面だけにするのを戻す) には渡さない。
        e.stopPropagation();
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const list = menuItems();
      if (list.length === 0) return;
      e.preventDefault();
      const at = list.findIndex((el) => el === document.activeElement);
      const next =
        e.key === "ArrowDown" ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="study-viewer-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="study-viewer-btn study-viewer-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="そのほかの操作"
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open ? (
        <div className="app-menu-list study-viewer-menu-list">
          <div role="menu" aria-label="そのほかの操作">
            {controls.map((c) => (
              <button
                key={c.id}
                type="button"
                {...(c.pressed === undefined
                  ? { role: "menuitem" as const }
                  : { role: "menuitemcheckbox" as const, "aria-checked": c.pressed })}
                className="app-menu-item"
                {...(c.count !== undefined
                  ? { "aria-label": `${c.label}（${c.count}件）` }
                  : c.ariaLabel !== undefined
                    ? { "aria-label": c.ariaLabel }
                    : {})}
                onClick={() => {
                  setOpen(false);
                  c.onSelect();
                }}
              >
                <span className="study-viewer-menu-mark" aria-hidden="true">
                  {c.pressed ? "✓" : ""}
                </span>
                {c.label}
                {c.count === undefined ? null : (
                  <span className="study-viewer-menu-count" aria-hidden="true">
                    {c.count}
                  </span>
                )}
              </button>
            ))}
            {panels.map((p) => (
              <button
                key={p.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={openPanelId === p.id}
                className="app-menu-item"
                onClick={() => {
                  setOpen(false);
                  onSelectPanel(p.id);
                }}
              >
                <span className="study-viewer-menu-mark" aria-hidden="true">
                  {openPanelId === p.id ? "✓" : ""}
                </span>
                {p.label}
              </button>
            ))}
          </div>
          {extra ? <div className="study-viewer-menu-extra">{extra}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
